import { useState, useEffect, useMemo } from 'react';
import { CalendarTimeEntry, CalendarMonthData } from '../../services/types';
import { getCalendarMonth } from '../../services/api';
import {
  shortDays,
  shortMonthNames,
  normalizeCompanyName,
  formatHours,
  getCompanyColor,
} from './calendarUtils';
import { ViewMode } from './ViewSelector';

interface YearViewProps {
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  selectedResourceId: number | null;
  selectedCompanies: Set<string>;
  searchFilter?: (entry: CalendarTimeEntry) => boolean;
  setViewMode: (mode: ViewMode) => void;
  onEntryClick?: (entry: CalendarTimeEntry, event: React.MouseEvent) => void;
  onAddEntry?: (date: Date) => void;
}

interface MonthDataCache {
  [key: string]: CalendarMonthData | null;
}

type DotMode = 'hours' | 'entries';

interface DayPopup {
  date: Date;
  entries: CalendarTimeEntry[];
  position: { x: number; y: number };
}

export default function YearView({
  currentDate,
  setCurrentDate,
  selectedResourceId,
  selectedCompanies,
  searchFilter,
  setViewMode,
  onEntryClick,
  onAddEntry,
}: YearViewProps) {
  const year = currentDate.getFullYear();
  const [monthDataCache, setMonthDataCache] = useState<MonthDataCache>({});
  const [loading, setLoading] = useState(true);
  const [dotMode, setDotMode] = useState<DotMode>('hours');
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [dayPopup, setDayPopup] = useState<DayPopup | null>(null);

  // Fetch all 12 months for the year
  useEffect(() => {
    let cancelled = false;

    async function fetchYear() {
      if (!selectedResourceId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const cache: MonthDataCache = {};

      // Fetch all 12 months in parallel
      const promises = Array.from({ length: 12 }, (_, i) =>
        getCalendarMonth(year, i + 1, selectedResourceId).catch(() => null)
      );

      const results = await Promise.all(promises);

      if (!cancelled) {
        for (let i = 0; i < 12; i++) {
          cache[`${year}-${i + 1}`] = results[i];
        }
        setMonthDataCache(cache);
        setLoading(false);
      }
    }

    fetchYear();
    return () => { cancelled = true; };
  }, [year, selectedResourceId]);

  // Calculate activity data for each day of the year
  const yearActivity = useMemo(() => {
    const activity: Record<string, { hours: number; entries: CalendarTimeEntry[] }> = {};

    for (let month = 1; month <= 12; month++) {
      const monthData = monthDataCache[`${year}-${month}`];
      if (!monthData?.entries) continue;

      for (const entry of monthData.entries) {
        if (selectedCompanies.size > 0 && !selectedCompanies.has(normalizeCompanyName(entry.companyName))) {
          continue;
        }
        if (searchFilter && !searchFilter(entry)) {
          continue;
        }

        const dateKey = entry.dateWorked;
        if (!activity[dateKey]) {
          activity[dateKey] = { hours: 0, entries: [] };
        }
        activity[dateKey].hours += entry.hoursWorked;
        activity[dateKey].entries.push(entry);
      }
    }

    return activity;
  }, [year, monthDataCache, selectedCompanies, searchFilter]);

  const today = new Date();
  const isCurrentYear = year === today.getFullYear();

  // Get activity level (0-4) for color intensity
  const getActivityLevel = (hours: number, entryCount: number): number => {
    if (dotMode === 'hours') {
      if (hours >= 8) return 4;
      if (hours >= 6) return 3;
      if (hours >= 4) return 2;
      if (hours >= 2) return 1;
      if (hours > 0) return 1;
      return 0;
    } else {
      if (entryCount >= 4) return 4;
      if (entryCount >= 3) return 3;
      if (entryCount >= 2) return 2;
      if (entryCount >= 1) return 1;
      return 0;
    }
  };

  // Get background color based on activity level
  const getActivityBgClass = (level: number): string => {
    switch (level) {
      case 1: return 'bg-blue-900/40';
      case 2: return 'bg-blue-800/60';
      case 3: return 'bg-blue-700/70';
      case 4: return 'bg-blue-600/80';
      default: return '';
    }
  };

  // Handle clicking on a day - show popup with entries
  const handleDayClick = (date: Date, entries: CalendarTimeEntry[], event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedDay(date);

    if (entries.length > 0) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      setDayPopup({
        date,
        entries,
        position: { x: rect.left + rect.width / 2, y: rect.bottom + 8 }
      });
    } else {
      setDayPopup(null);
    }
  };

  // Handle clicking on a month name
  const handleMonthClick = (month: number) => {
    setCurrentDate(new Date(year, month, 1));
    setViewMode('month');
  };

  // Close popup when clicking outside
  const handleBackdropClick = () => {
    setDayPopup(null);
    setSelectedDay(null);
  };

  // Generate days for a mini calendar month
  const getMiniCalendarDays = (month: number) => {
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    while (days.length < 42) days.push(null);

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-slate-400 text-sm">Loading year data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative" onClick={handleBackdropClick}>
      {/* Header with toggle */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700 bg-slate-800/50">
        <div className="text-lg font-medium text-white">
          {year}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Show by:</span>
          <div className="flex rounded-lg overflow-hidden border border-slate-600">
            <button
              onClick={(e) => { e.stopPropagation(); setDotMode('hours'); }}
              className={`px-3 py-1 text-xs transition-colors ${
                dotMode === 'hours'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-400 hover:text-white'
              }`}
            >
              Hours
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setDotMode('entries'); }}
              className={`px-3 py-1 text-xs transition-colors ${
                dotMode === 'entries'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-400 hover:text-white'
              }`}
            >
              Entries
            </button>
          </div>
        </div>
      </div>

      {/* 12 Mini Calendars Grid (4x3) */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 12 }, (_, monthIndex) => {
            const days = getMiniCalendarDays(monthIndex);
            const isCurrentMonth = isCurrentYear && monthIndex === today.getMonth();

            return (
              <div key={monthIndex} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                {/* Month Name */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleMonthClick(monthIndex); }}
                  className={`text-sm font-medium mb-2 hover:text-blue-400 transition-colors ${
                    isCurrentMonth ? 'text-blue-400' : 'text-white'
                  }`}
                >
                  {shortMonthNames[monthIndex]}
                </button>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-0 mb-1">
                  {shortDays.map((d, i) => (
                    <div key={i} className="text-[9px] text-slate-600 text-center">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-0.5">
                  {days.map((day, idx) => {
                    if (day === null) {
                      return <div key={idx} className="w-5 h-5" />;
                    }

                    const dateKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const activity = yearActivity[dateKey];
                    const isToday = isCurrentYear && monthIndex === today.getMonth() && day === today.getDate();
                    const thisDate = new Date(year, monthIndex, day);
                    const isSelected = selectedDay && thisDate.toDateString() === selectedDay.toDateString();

                    const activityLevel = activity ? getActivityLevel(activity.hours, activity.entries.length) : 0;
                    const activityBg = getActivityBgClass(activityLevel);

                    return (
                      <button
                        key={idx}
                        onClick={(e) => handleDayClick(thisDate, activity?.entries || [], e)}
                        className={`w-5 h-5 flex items-center justify-center rounded text-[10px] transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white font-medium ring-2 ring-blue-400'
                            : isToday
                              ? 'bg-slate-600 text-white font-medium'
                              : activityLevel > 0
                                ? `${activityBg} text-slate-300 hover:ring-1 hover:ring-slate-500`
                                : 'text-slate-500 hover:bg-slate-700'
                        }`}
                        title={activity ? `${activity.hours.toFixed(1)}h, ${activity.entries.length} entries` : 'No entries'}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex-shrink-0 px-4 py-2 border-t border-slate-700 bg-slate-800/50">
        <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
          <span>Activity:</span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-900/40" />
            {dotMode === 'hours' ? '0-2h' : '1'}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-800/60" />
            {dotMode === 'hours' ? '2-4h' : '2'}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-700/70" />
            {dotMode === 'hours' ? '4-6h' : '3'}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-600/80" />
            {dotMode === 'hours' ? '8h+' : '4+'}
          </span>
        </div>
      </div>

      {/* Day Popup Modal */}
      {dayPopup && (
        <div
          className="fixed z-50 bg-slate-800 rounded-lg border border-slate-600 shadow-2xl min-w-64 max-w-80"
          style={{
            left: Math.min(dayPopup.position.x - 128, window.innerWidth - 340),
            top: dayPopup.position.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Popup Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
            <div className="font-medium text-white text-sm">
              {dayPopup.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            <button
              onClick={() => { setDayPopup(null); setSelectedDay(null); }}
              className="text-slate-400 hover:text-white p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Popup Entries */}
          <div className="p-2 space-y-1 max-h-48 overflow-y-auto">
            {dayPopup.entries.map((entry, idx) => {
              const color = getCompanyColor(entry.companyName);
              return (
                <button
                  key={entry.id || idx}
                  onClick={(e) => onEntryClick?.(entry, e)}
                  className="w-full text-left px-2 py-1.5 rounded bg-slate-700/60 hover:bg-slate-700 border-l-2 transition-colors"
                  style={{ borderLeftColor: color.hex }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-300 truncate">{entry.companyName}</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{formatHours(entry.hoursWorked)}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="px-3 py-2 border-t border-slate-700 flex items-center justify-between">
            {onAddEntry && (
              <button
                onClick={() => {
                  onAddEntry(dayPopup.date);
                  setDayPopup(null);
                  setSelectedDay(null);
                }}
                className="text-xs text-emerald-400 hover:text-emerald-300"
              >
                + Add Entry
              </button>
            )}
            <button
              onClick={() => {
                setCurrentDate(dayPopup.date);
                setViewMode('day');
              }}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Open in Day view →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
